import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserSquare2, ChevronLeft, Loader2, Film, X, Plus, Pencil, Sparkles, Trash2 } from "lucide-react";
import RenameModal from "@/components/RenameModal";
import DeleteButton from "@/components/DeleteButton";

/**
 * `/owners/:name` — detail page. Lists every horse tied to this owner
 * (majority-vote across their clips) plus an "Attach horse" action to
 * pull an existing horse over from another owner or from Unassigned.
 */
export default function OwnerDetail() {
  const { name: rawName } = useParams();
  const name = decodeURIComponent(rawName || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAttach, setShowAttach] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    return api.get(`/owners/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <Link to="/owners" className="btn-secondary inline-flex items-center gap-1.5 mb-4" data-testid="back-to-owners">
          <ChevronLeft size={13} /> Back to Owners
        </Link>
        <div className="border border-dashed border-stone-700 p-8 text-center text-stone-500">
          Owner not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <Link to="/owners" className="btn-secondary inline-flex items-center gap-1.5 mb-4" data-testid="back-to-owners">
        <ChevronLeft size={13} /> Back to Owners
      </Link>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <div className="label-tiny mb-2">Owner</div>
          <div className="flex items-center gap-2 flex-wrap">
            <UserSquare2 size={30} className="text-orange-500 shrink-0" />
            <h1 className="font-display uppercase text-5xl font-bold leading-[0.95] break-words">
              {data.name}
            </h1>
            <button
              type="button"
              onClick={() => setRenaming(true)}
              className="p-2 border border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 transition-colors ml-2"
              title="Rename owner (cascades across every clip)"
              data-testid="owner-rename-btn"
            >
              <Pencil size={13} />
            </button>
            <DeleteButton
              testId="owner-detail-delete-btn"
              label={data.name}
              size={13}
              className="!p-2 ml-1"
              onDelete={async () => {
                await api.delete(`/owners/${encodeURIComponent(name)}`);
                navigate("/owners");
              }}
              successMessage={`"${data.name}" removed`}
            />
          </div>
          {data.description && <p className="text-sm text-stone-400 mt-3 max-w-2xl">{data.description}</p>}
          <div className="text-xs text-stone-400 mt-3 flex flex-wrap gap-x-3 gap-y-1 font-mono-tc">
            <span className="inline-flex items-center gap-1">{data.horse_count} horse{data.horse_count === 1 ? "" : "s"}</span>
            <span className="inline-flex items-center gap-1"><Film size={11} /> {data.clip_count} clips</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="font-display uppercase text-lg">Horses under this owner</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
            data-testid="create-horse-btn"
          >
            <Sparkles size={11} /> Add horse
          </button>
          <button
            onClick={() => setShowAttach(true)}
            className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1 hover:!text-orange-400"
            data-testid="attach-horse-btn"
          >
            <Plus size={11} /> Attach existing
          </button>
        </div>
      </div>

      {data.horses.length === 0 ? (
        <div className="border border-dashed border-stone-700 p-6 text-center text-stone-500 text-sm">
          No horses tied yet. Click <strong className="text-orange-400">Add horse</strong> to create a fresh one under this owner,
          or <strong className="text-orange-400">Attach existing</strong> to pull one that already has clips.
        </div>
      ) : (
        <div className="border border-stone-800 divide-y divide-stone-800" data-testid="owner-horses-list">
          {data.horses.map((h) => (
            <OwnerHorseRow
              key={h.name}
              horse={h}
              ownerName={name}
              onOpen={() => navigate(`/horses/${encodeURIComponent(h.name)}`)}
              onRemoved={load}
            />
          ))}
        </div>
      )}

      {showAttach && (
        <AttachHorseModal
          ownerName={name}
          alreadyAttachedLower={new Set(data.horses.map((h) => h.name.toLowerCase()))}
          onClose={() => setShowAttach(false)}
          onAttached={() => { setShowAttach(false); load(); }}
        />
      )}

      {showCreate && (
        <CreateHorseModal
          ownerName={name}
          onClose={() => setShowCreate(false)}
          onCreated={(horseName) => {
            setShowCreate(false);
            toast.success(`"${horseName}" added under ${name}`);
            navigate(`/horses/${encodeURIComponent(horseName)}`);
          }}
        />
      )}

      {renaming && (
        <RenameModal
          label="Rename Owner"
          currentName={data.name}
          subtitle="Cascades across every clip, every horse tied to this owner, and the owners directory."
          testIdPrefix="owner-rename"
          maxLen={120}
          onSave={async (newName) => {
            try {
              const r = await api.put(
                `/owners/${encodeURIComponent(data.name)}/rename`,
                { new_name: newName },
              );
              const c = r.data?.clips_updated ?? 0;
              const h = r.data?.horses_updated ?? 0;
              toast.success(`Renamed · ${c} clip${c === 1 ? "" : "s"} · ${h} horse${h === 1 ? "" : "s"} updated`);
              navigate(`/owners/${encodeURIComponent(newName)}`, { replace: true });
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Rename failed");
              throw err;
            }
          }}
          onClose={() => setRenaming(false)}
        />
      )}
    </div>
  );
}

function AttachHorseModal({ ownerName, alreadyAttachedLower, onClose, onAttached }) {
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/horses")
      .then((r) => setHorses(r.data.horses || []))
      .catch(() => setHorses([]))
      .finally(() => setLoading(false));
  }, []);

  const attach = async (horse) => {
    setSaving(horse.name);
    try {
      const r = await api.post(`/owners/${encodeURIComponent(ownerName)}/horses`, { horse_name: horse.name });
      const n = r.data?.clips_updated ?? 0;
      toast.success(`Attached "${horse.name}" · ${n} clip${n === 1 ? "" : "s"} updated`);
      onAttached();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attach failed");
    } finally {
      setSaving(null);
    }
  };

  const filtered = q.trim()
    ? horses.filter((h) => (h.name || "").toLowerCase().includes(q.toLowerCase()))
    : horses;

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="attach-horse-modal">
      <div className="bg-stone-950 border border-orange-700 max-w-md w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div>
            <div className="label-tiny">Attach horse to</div>
            <div className="font-display uppercase text-lg leading-tight break-words">{ownerName}</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={16} /></button>
        </div>
        <div className="p-3 border-b border-stone-800">
          <input
            autoFocus
            placeholder="Search horses…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="field-input w-full"
            data-testid="attach-horse-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto min-h-[240px]">
          {loading ? (
            <div className="p-4 text-xs text-stone-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-xs text-stone-500 text-center">
              {q.trim() ? "No matches." : "No horses in the Library yet."}
            </div>
          ) : (
            filtered.map((h) => {
              const already = alreadyAttachedLower.has(h.name.toLowerCase());
              const busy = saving === h.name;
              return (
                <button
                  key={h.name}
                  onClick={() => !already && attach(h)}
                  disabled={already || busy}
                  className={`w-full text-left px-4 py-2 border-b border-stone-800/70 flex items-center gap-2 ${
                    already ? "opacity-40 cursor-not-allowed" : "hover:bg-stone-900"
                  }`}
                  data-testid={`attach-horse-row-${h.name.replace(/\s+/g, "_")}`}
                >
                  <span className="text-lg">🐎</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-stone-200 truncate">{h.name}</div>
                    <div className="text-[11px] text-stone-500 font-mono-tc">
                      {h.clip_count} clips
                      {h.owner_name && (
                        <span className={`ml-2 ${h.owner_name.toLowerCase() === ownerName.toLowerCase() ? "text-orange-400" : "text-amber-500"}`}>
                          · owner: {h.owner_name}
                        </span>
                      )}
                      {!h.owner_name && <span className="ml-2 text-stone-500">· unassigned</span>}
                    </div>
                  </div>
                  {busy ? <Loader2 size={12} className="animate-spin text-orange-400" /> : <Plus size={12} className={already ? "text-stone-600" : "text-orange-500"} />}
                </button>
              );
            })
          )}
        </div>
        <div className="p-3 text-[10px] text-stone-500 border-t border-stone-800">
          Attaching also retags every clip that horse appears in with this owner.
        </div>
      </div>
    </div>
  );
}


function CreateHorseModal({ ownerName, onClose, onCreated }) {
  const [horseName, setHorseName] = useState("");
  const [breed, setBreed] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const nm = horseName.trim();
    if (!nm) {
      toast.error("Horse name required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/owners/${encodeURIComponent(ownerName)}/horses/create`, {
        horse_name: nm,
        breed: breed.trim(),
        description: description.trim(),
      });
      onCreated(nm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create horse");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-horse-modal"
    >
      <form
        onSubmit={submit}
        className="bg-stone-950 border border-orange-700 max-w-md w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div>
            <div className="label-tiny">Add new horse under</div>
            <div className="font-display uppercase text-lg leading-tight break-words">{ownerName}</div>
          </div>
          <button type="button" onClick={onClose} className="text-stone-500 hover:text-stone-200">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <div className="label-tiny mb-1.5">Horse name *</div>
            <input
              autoFocus
              value={horseName}
              onChange={(e) => setHorseName(e.target.value)}
              placeholder="e.g. Buckshot"
              maxLength={120}
              className="field-input w-full"
              data-testid="create-horse-name"
            />
          </div>
          <div>
            <div className="label-tiny mb-1.5">Breed <span className="text-stone-600 normal-case">(optional)</span></div>
            <input
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              placeholder="e.g. Quarter Horse"
              maxLength={80}
              className="field-input w-full"
              data-testid="create-horse-breed"
            />
          </div>
          <div>
            <div className="label-tiny mb-1.5">Description <span className="text-stone-600 normal-case">(optional)</span></div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes, lineage, temperament, etc."
              maxLength={1000}
              rows={3}
              className="field-input w-full resize-none"
              data-testid="create-horse-description"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-stone-800">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs"
            data-testid="create-horse-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !horseName.trim()}
            className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="create-horse-submit"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {saving ? "Creating…" : "Create horse"}
          </button>
        </div>
      </form>
    </div>
  );
}


function OwnerHorseRow({ horse, ownerName, onOpen, onRemoved }) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  const remove = async (e) => {
    e.stopPropagation();
    setRemoving(true);
    try {
      const r = await api.delete(
        `/owners/${encodeURIComponent(ownerName)}/horses/${encodeURIComponent(horse.name)}`,
      );
      const n = r.data?.clips_updated ?? 0;
      const wiped = r.data?.horse_row_deleted;
      toast.success(
        wiped
          ? `"${horse.name}" removed (0 clips — row deleted)`
          : `"${horse.name}" detached from ${ownerName} · ${n} clip${n === 1 ? "" : "s"} unassigned`,
      );
      onRemoved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setRemoving(false);
      setConfirming(false);
    }
  };

  return (
    <div
      className="w-full px-4 py-3 hover:bg-stone-900 transition-colors flex items-center gap-3 group"
      data-testid={`owner-horse-row-${horse.name.replace(/\s+/g, "_")}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex items-center gap-3 min-w-0 flex-1 text-left"
        data-testid={`owner-horse-open-${horse.name.replace(/\s+/g, "_")}`}
      >
        <span className="text-xl">🐎</span>
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-sm font-semibold truncate">{horse.name}</div>
          <div className="text-[11px] text-stone-500 font-mono-tc">
            {horse.clip_count} clip{horse.clip_count === 1 ? "" : "s"}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-stone-500 mr-3">View →</span>
      </button>

      {confirming ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-amber-400 mr-1">Remove?</span>
          <button
            type="button"
            onClick={remove}
            disabled={removing}
            className="text-[10px] px-2 py-1 border border-red-700 bg-red-900/30 text-red-300 hover:bg-red-900/60 uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`owner-horse-remove-confirm-${horse.name.replace(/\s+/g, "_")}`}
          >
            {removing ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Yes
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
            className="text-[10px] px-2 py-1 border border-stone-700 text-stone-400 hover:text-stone-200 uppercase tracking-widest"
            data-testid={`owner-horse-remove-cancel-${horse.name.replace(/\s+/g, "_")}`}
          >
            No
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 border border-stone-800 text-stone-500 hover:text-red-400 hover:border-red-700 shrink-0"
          title={horse.clip_count > 0 ? `Detach from ${ownerName} (keeps ${horse.clip_count} clip${horse.clip_count === 1 ? "" : "s"} but clears owner)` : `Delete this empty horse row`}
          data-testid={`owner-horse-remove-${horse.name.replace(/\s+/g, "_")}`}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}
